<template>
    <div class="rc-base-input">
        <div
            v-if="['text', 'email', 'tel', 'birthday'].includes(type)"
            class="input-wrapper"
        >
            <slot name="label">
                <label
                    :for="input_id"
                    class="input-label"
                    :class="{ 'required': requiredBool, 'underlined': !outlined }"
                >
                    <span>
                        {{ label }}
                    </span>
                    <span
                        v-if="requiredBool"
                        class="required-indicator"
                        aria-hidden="true"
                    >*</span>
                </label>
            </slot>

            <div class="input-container" :class="{ 'has-icon': icon }">
                <v-icon
                    v-if="icon"
                    class="input-icon"
                    :class="{ 'input-icon-focused': is_focused }"
                    size="20"
                >{{ icon }}</v-icon>
                <input
                    :id="input_id"
                    :type="inputType"
                    :value="displayValue"
                    :disabled="disabled"
                    :readonly="readonly"
                    :required="requiredBool"
                    :autocomplete="calculatedAutocomplete"
                    v-bind="{
                        ...(placeholder ? { placeholder } : {}),
                        ...(describedByIds?.length ? { 'aria-describedby': describedByIds } : {}),
                        ...(hasError ? { 'aria-invalid': 'true' } : {}),
                    }"
                    :inputmode="inputMode"
                    :pattern="pattern"
                    :maxlength="maxlength"
                    class="form-input"
                    :class="[inputClasses, { 'has-prepend-icon': icon }]"
                    @input="onInput"
                    @blur="onBlur"
                    @focus="onFocus"
                    @keydown="onKeydown"
                    ref="input"
                />

                <div
                    v-if="$slots['append-outer']"
                    class="append-outer-slot"
                >
                    <slot name="append-outer"></slot>
                </div>
            </div>

            <div
                v-if="hasError"
                class="error-message"
                :id="`${input_id}-error`"
                role="alert"
            >
                {{ displayErrorMessage }}
            </div>

            <div
                v-if="inputHint && !hasError"
                class="hint-text"
                :id="`${input_id}-hint`"
            >
                {{ inputHint }}
            </div>
        </div>

        <div
            v-else-if="type === 'select'"
            class="select-wrapper"
        >
            <slot name="label">
                <label
                    :id="`${input_id}-label`"
                    class="input-label"
                    :class="{ 'required': requiredBool, 'underlined': !outlined }"
                >
                    {{ label }}
                    <span
                        v-if="requiredBool"
                        class="required-indicator"
                        aria-hidden="true"
                    >*</span>
                </label>
            </slot>

            <div
                v-if="!multiple"
                class="select-container"
                :class="{
                    'select-disabled': disabled,
                    'select-error': hasError,
                    'select-outlined': outlined,
                    'select-underlined': !outlined,
                    'select-dropdown-open': dropdown_open,
                    'select-dropdown-open-top': dropdown_open && dropdownPlacement === 'top',
                    'select-dropdown-open-bottom': dropdown_open && dropdownPlacement !== 'top'
                }"
            >
                <button
                    type="button"
                    ref="selectToggle"
                    class="form-select-button"
                    :class="{ 'has-clear-button': showClearButton }"
                    :tabindex="disabled ? -1 : 0"
                    :aria-expanded="dropdown_open"
                    :aria-haspopup="'listbox'"
                    :aria-labelledby="`${input_id}-label`"
                    :aria-describedby="describedByIds"
                    :aria-invalid="hasError"
                    :disabled="disabled"
                    @click="toggleDropdown"
                    @keydown="onSelectKeydown"
                    @blur="onSelectBlur"
                    @focus="onSelectFocus"
                >
                    <span
                        v-if="!htmlOptions"
                        class="select-value"
                        :class="{ 'placeholder': selectValueIsPlaceholder }"
                    >
                        {{ selectDisplayValue }}
                    </span>
                    <span
                        v-else
                        class="select-value"
                        :class="{ 'placeholder': selectValueIsPlaceholder }"
                        v-html="selectDisplayValue"
                    >
                    </span>

                    <button
                        v-if="showClearButton"
                        type="button"
                        class="select-clear-button"
                        :aria-label="`Clear ${label}`"
                        :disabled="disabled || requiredBool"
                        @click.stop="clearSelection"
                        @keydown="onClearKeydown"
                        @mousedown.prevent
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>

                    <div
                        class="select-arrow"
                        aria-hidden="true"
                    >
                        <svg
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                            fill="none"
                            :class="{ 'rotated': dropdown_open }"
                        >
                            <path
                                d="M1 1L6 6L11 1"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                </button>

                <div
                    v-if="dropdown_open"
                    class="select-dropdown"
                    :class="{ 'select-dropdown-top': dropdownPlacement === 'top' }"
                    role="listbox"
                    :aria-labelledby="`${input_id}-label`"
                >
                    <div
                        v-for="(option, index) in options"
                        :key="getOptionValue(option)"
                        :ref="`selectOption-${index}`"
                        class="select-option"
                        :class="{
                            'selected': selected_value === getOptionValue(option),
                            'focused': focused_option_index === index
                        }"
                        role="option"
                        :tabindex="dropdown_open ? (focused_option_index === index ? 0 : -1) : -1"
                        :aria-selected="selected_value === getOptionValue(option)"
                        :aria-labelledby="`${input_id}-select-option-${index}`"
                        @click="selectOption(getOptionValue(option))"
                        @mousedown.prevent
                        @keydown="onSelectOptionKeydown($event, getOptionValue(option), index)"
                        @blur="onSelectOptionBlur"
                    >
                        <span
                            v-if="!htmlOptions"
                            class="select-option-label"
                            :id="`${input_id}-select-option-${index}`"
                        >
                            {{ getOptionLabel(option) }}
                        </span>
                        <span
                            v-else
                            class="select-option-label"
                            :id="`${input_id}-select-option-${index}`"
                            v-html="getOptionLabel(option)"
                        >
                        </span>
                    </div>
                </div>
            </div>

            <div
                v-else
                class="multi-select-container"
                :class="{
                    'multi-select-disabled': disabled,
                    'multi-select-error': hasError,
                    'multi-select-outlined': outlined,
                    'multi-select-underlined': !outlined,
                    'multi-select-dropdown-open': dropdown_open,
                    'multi-select-dropdown-open-top': dropdown_open && dropdownPlacement === 'top',
                    'multi-select-dropdown-open-bottom': dropdown_open && dropdownPlacement !== 'top'
                }"
            >
                <div
                    role="combobox"
                    ref="multiSelectToggle"
                    class="multi-select-toggle"
                    :tabindex="disabled ? -1 : 0"
                    :aria-expanded="dropdown_open"
                    :aria-haspopup="'listbox'"
                    :aria-labelledby="`${input_id}-label`"
                    :aria-describedby="describedByIds"
                    :aria-invalid="hasError"
                    :title="multiSelectTooltipText"
                    @click="toggleDropdown"
                    @keydown="onSelectKeydown"
                    @blur="onSelectBlur"
                    @focus="onSelectFocus"
                >
                    <span
                        v-if="!htmlOptions"
                        class="multi-select-value"
                        :class="{ 'placeholder': multiSelectValueIsPlaceholder }"
                    >
                        {{ multiSelectDisplayValue }}
                    </span>
                    <span
                        v-else
                        class="multi-select-value"
                        :class="{ 'placeholder': multiSelectValueIsPlaceholder }"
                        v-html="multiSelectDisplayValue"
                    >
                    </span>

                    <div
                        class="multi-select-arrow"
                        aria-hidden="true"
                    >
                        <svg
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                            fill="none"
                            :class="{ 'rotated': dropdown_open }"
                        >
                            <path
                                d="M1 1L6 6L11 1"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                </div>

                <div
                    v-if="dropdown_open"
                    class="multi-select-dropdown"
                    :class="{ 'multi-select-dropdown-top': dropdownPlacement === 'top' }"
                    role="listbox"
                    :aria-multiselectable="true"
                >
                    <div
                        v-for="(option, index) in options"
                        :key="getOptionValue(option)"
                        class="multi-select-option"
                        :class="{
                            'selected': isOptionSelected(getOptionValue(option)),
                            'focused': focused_option_index === index
                        }"
                        role="option"
                        :tabindex="dropdown_open ? (focused_option_index === index ? 0 : -1) : -1"
                        :aria-selected="isOptionSelected(getOptionValue(option))"
                        :aria-labelledby="`${input_id}-option-${index}`"
                        @click="toggleOption(getOptionValue(option))"
                        @mousedown.prevent
                        @keydown="onOptionKeydown($event, getOptionValue(option), index)"
                        @blur="onSelectOptionBlur"
                        :ref="`option-${index}`"
                    >
                        <input
                            type="checkbox"
                            :checked="isOptionSelected(getOptionValue(option))"
                            :disabled="disabled"
                            tabindex="-1"
                            class="multi-select-checkbox"
                            :aria-hidden="true"
                        />
                        <span
                            v-if="!htmlOptions"
                            class="multi-select-option-label"
                            :id="`${input_id}-option-${index}`"
                        >
                            {{ getOptionLabel(option) }}
                        </span>
                        <span
                            v-else
                            class="multi-select-option-label"
                            :id="`${input_id}-option-${index}`"
                            v-html="getOptionLabel(option)"
                        >
                        </span>
                    </div>
                </div>
            </div>

            <div
                v-if="hasError"
                class="error-message"
                :id="`${input_id}-error`"
                role="alert"
            >
                {{ displayErrorMessage }}
            </div>

            <div
                v-if="inputHint && !hasError"
                class="hint-text"
                :id="`${input_id}-hint`"
            >
                {{ inputHint }}
            </div>

            <div
                v-if="type === 'select' && multiple"
                class="sr-only"
                aria-live="polite"
                aria-atomic="true"
            >
                {{ screen_reader_announcement }}
            </div>
        </div>
    </div>
</template>

<script>
/**
 * rc-base-input
 *
 * emits 'input' event with new value on change
 * emits 'validated' event with boolean validity on validation (for tel, email, birthday types)
 * emits 'focus', 'blur', and 'keydown' events as well
 * for select type, emits 'input' event with selected value (or array of values if multiple) on change
 *
 * This is a custom, accessibility-focused form input with no external UI dependencies.
 * Supports text, email, tel, birthday, and select types with built-in validation, autocomplete, and input mode for email, tel, and birthday (MM/DD/YYYY).
 * Includes built-in hint for birthday input type.
 * Is optimized for AA accessibility compliance.
 *
 * It was originally built for use in public forms where accessibility couldn't be achieved via Vuetify 2 wrappers.
 * However it can also be used in the redchirp app itself.
 * NOTE: this does look different in some (pretty subtle) ways from the Vuetify 2 inputs it replaces.
 * When using this, at least ensure that all other inputs in the same form are also this component, to maintain visual consistency.
 * Additionally, the 'select' functionality is currently rather basic and may need to be enhanced for more complex use cases.
 * * Autocomplete reference: https://www.w3.org/TR/WCAG22/#input-purposes
 */

import E164 from '@/js/E164.js';
import Validate from '@/js/validate.js';

export default {
    name: 'RcBaseInput',

    props: {
        value: {
            type: [String, Number, Array],
            default: ''
        },
        label: {
            type: String,
            required: true
        },
        type: {
            type: String,
            default: 'text',
            validator: value => ['text', 'email', 'tel', 'select', 'birthday'].includes(value)
        },
        /** * Corresponds to the native inputMode attribute
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/inputmode
         */
        mode: {
            type: String,
            default: 'text'
        },
        placeholder: {
            type: String,
            default: ''
        },
        hint: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: true
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        /**
         * Can be bool or the string optional/required in the case of custom forms
         */
        required: {
            type: [Boolean, String],
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        errorMessage: {
            type: String,
            default: ''
        },

        /**
         * Corresponds to the native autocomplete attribute
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
         * 
         * Set to null or false to omit the autocomplete attribute entirely.
         * Autocomplete is already handled for text, email, tel, and birthday types if not set here.
         */
        autocomplete: {
            type: [String, Boolean],
            default: null
        },
        pattern: {
            type: String,
            default: null
        },
        maxlength: {
            type: [String, Number],
            default: null
        },

        /**
         * Icon to display on the left side of the input
         * Uses Material Design Icons (mdi-*)
         */
        icon: {
            type: String,
            default: null
        },

        /**
         * For birthday type, the minimum age required.
         */
        minAge: {
            type: Number,
            default: null
        },

        /* props for "select" type */
        options: {
            type: Array,
            default: () => []
        },
        optionValue: {
            type: String,
            default: 'value'
        },
        optionLabel: {
            type: String,
            default: 'label'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        htmlOptions: {
            type: Boolean,
            default: false
        },
        dropdownPlacement: {
            type: String,
            default: 'bottom',
            validator: value => ['top', 'bottom'].includes(value)
        },
    },

    data() {
        return {
            input_id: `input-${Math.random().toString(36).substr(2, 9)}`,
            is_focused: false,
            local_value: this.value,
            phone_error_message: '',
            phone_touched: false,
            phone_valid: true,
            raw_digits: '',
            email_error_message: '',
            email_touched: false,
            email_valid: true,
            birthday_touched: false,
            birthday_error_message: '',
            birthday_valid: true,
            dropdown_open: false,
            focused_option_index: -1,
            selected_value: this.multiple ?
                (Array.isArray(this.value) ? this.value : []) :
                this.value,
            screen_reader_announcement: ''
        }
    },

    computed: {
        requiredBool() {
            // can sometimes come in as "optional"
            return this.required === true || this.required === 'required';
        },

        calculatedAutocomplete() {
            if (this.autocomplete === false) return null; // can pass false to forgo attribute on DOM el entirely
            if (this.autocomplete) return this.autocomplete; // if explicitly provided, that wins out

            if (this.type === 'email') return 'email';
            if (this.type === 'birthday') return 'bday';
            if (this.type === 'tel') return 'tel';

            return null;
        },

        inputType() {
            if (this.type === 'birthday') return 'tel'
            return this.type
        },

        inputMode() {
            if (this.type === 'tel') return 'tel'
            if (this.type === 'birthday') return 'numeric'
            if (this.type === 'email') return 'email'
            return this.mode
        },

        inputHint() {
            if (this.hint) return this.hint; // if explicitly provided, that wins out

            if (this.type === 'birthday') {
                return 'MM/DD/YYYY -- use a leading zero (e.g. 01 for January). slashes will be added automatically'
            }

            return '';
        },

        hasError() {
            if (this.type === 'tel') {
                return this.error || (this.phone_touched && !this.phone_valid) || (this.phone_error_message && this.phone_error_message.length > 0)
            }
            if (this.type === 'email') {
                return this.error || (this.email_touched && !this.email_valid) || (this.email_error_message && this.email_error_message.length > 0)
            }
            if (this.type === 'birthday') {
                return this.error || (this.birthday_touched && !this.birthday_valid) || (this.birthday_error_message && this.birthday_error_message.length > 0)
            }
            return this.error || (this.errorMessage && this.errorMessage.length > 0)
        },

        displayErrorMessage() {
            if (this.type === 'tel') {
                return this.phone_touched && !this.phone_valid && this.phone_error_message ? this.phone_error_message : ''
            }
            if (this.type === 'email') {
                return this.email_touched && !this.email_valid && this.email_error_message ? this.email_error_message : ''
            }
            if (this.type === 'birthday') {
                return this.birthday_touched && !this.birthday_valid && this.birthday_error_message ? this.birthday_error_message : ''
            }
            return this.errorMessage
        },

        inputClasses() {
            return {
                'input-focused': this.is_focused,
                'input-error': this.hasError,
                'input-disabled': this.disabled,
                'input-readonly': this.readonly,
                'input-outlined': this.outlined,
                'input-underlined': !this.outlined
            }
        },

        describedByIds() {
            const ids = []
            if (this.hint && !this.hasError) {
                ids.push(`${this.input_id}-hint`)
            }
            if (this.hasError) {
                ids.push(`${this.input_id}-error`)
            }
            return ids.join(' ')
        },

        displayValue() {
            if (['text', 'email', 'tel', 'birthday'].includes(this.type)) {
                return this.local_value
            }
            return this.value
        },

        multiSelectDisplayValue() {
            if (!this.multiple) return ''

            if (this.selected_value?.length === 0) {
                return this.placeholder || 'Select options...'
            }

            const selected_labels = this.selected_value.map(value => {
                const option = this.options.find(opt => this.getOptionValue(opt) === value)
                return option ? this.getOptionLabel(option) : value
            })

            return selected_labels.join(', ')
        },

        selectDisplayValue() {
            if (this.multiple) return ''

            if (!this.selected_value || this.selected_value === '') {
                return this.placeholder || 'Select an option...'
            }

            const option = this.options.find(opt => this.getOptionValue(opt) === this.selected_value);
            return option ? this.getOptionLabel(option) : this.selected_value
        },

        selectValueIsPlaceholder() {
            return this.multiple ?
                this.selected_value.length === 0 :
                (!this.selected_value || this.selected_value === '')
        },

        multiSelectValueIsPlaceholder() {
            return this.selected_value.length === 0
        },

        multiSelectTooltipText() {
            if (!this.multiple || this.selected_value.length === 0) {
                return null
            }

            if (this.selected_value.length > 1) {
                const selectedLabels = this.selected_value.map(value => {
                    const option = this.options.find(opt => this.getOptionValue(opt) === value)
                    return option ? this.getOptionLabel(option) : value
                })
                return `Selected: ${selectedLabels.join(', ')}`
            }

            return null
        },

        showClearButton() {
            return this.clearable &&
                !this.requiredBool &&
                !this.disabled &&
                this.type === 'select' &&
                !this.multiple &&
                !this.selectValueIsPlaceholder
        }
    },

    methods: {
        onInput(event) {
            let value = event.target.value

            if (this.type === 'tel') {
                this.handlePhoneInput(value)
            } else if (this.type === 'email') {
                this.handleEmailInput(value)
            } else if (this.type === 'birthday') {
                this.handleBirthdayInput(value)
            } else if (this.type === 'text') {
                this.local_value = value
                this.$emit('input', value)
            } else {
                this.local_value = value
                this.$emit('input', value)
            }
        },

        onFocus(event) {
            this.is_focused = true
            this.$emit('focus', event)
        },

        onBlur(event) {
            this.is_focused = false

            if (this.type === 'tel') {
                this.handlePhoneBlur()
            } else if (this.type === 'email') {
                this.handleEmailBlur(event.target.value)
            } else if (this.type === 'birthday') {
                this.handleBirthdayBlur()
            }

            this.$emit('blur', event)
        },

        onKeydown(event) {
            this.$emit('keydown', event)
        },

        getOptionValue(option) {
            if (typeof option === 'string' || typeof option === 'number') {
                return option
            }
            return option[this.optionValue]
        },

        getOptionLabel(option) {
            if (typeof option === 'string' || typeof option === 'number') {
                return option
            }
            return option[this.optionLabel]
        },

        getDigitsOnly(value) {
            return ('' + value).replace(/\D/g, '');
        },

        maskDigits(digits) {
            return this.$rc.maskPhoneNumber(digits);
        },

        // check for anything other than digits, spaces, parentheses, hyphens, plus
        hasInvalidCharacters(value) {
            if (!value) return false;
            return /[^0-9\s()\-+]/.test(value);
        },

        handlePhoneInput(value) {
            if (this.hasInvalidCharacters(value)) {
                this.phone_touched = true;
                this.phone_valid = false;
                this.phone_error_message = 'Invalid phone number';
                this.$emit('validated', this.phone_valid);
                return;
            }

            const digits = this.getDigitsOnly(value);
            this.raw_digits = digits;

            if (digits.length <= 11) {
                this.local_value = digits ? this.maskDigits(digits) : '';
            } else {
                this.local_value = value;
            }
            this.$emit('input', this.local_value);

            this.validatePhoneDigits();
        },

        handlePhoneBlur() {
            this.phone_touched = true;

            if (this.hasInvalidCharacters(this.local_value)) {
                this.phone_valid = false;
                this.phone_error_message = 'Invalid phone number';
                this.$emit('validated', this.phone_valid);
                return;
            }

            this.validatePhoneDigits();
        },

        validatePhoneDigits() {
            const north_america_message = 'Oops! Please enter a valid North American phone number (e.g. 1 (555) 123-4567).';

            if (!this.raw_digits) {
                this.phone_valid = !this.requiredBool;
                this.phone_error_message = (this.phone_touched && this.requiredBool) ? `${this.label || 'Phone number'} is required` : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            if (this.raw_digits.length < 10) {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            // ITU-T E.164 standard limits international phone numbers to 15 digits max
            if (this.raw_digits.length > 15) {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            if (this.raw_digits.length > 11) {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? north_america_message : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            // if 11 digits, must start with 1 (North America country code)
            if (this.raw_digits.length === 11 && this.raw_digits.charAt(0) !== '1') {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? north_america_message : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            let usableDigits = this.raw_digits;
            if (usableDigits.charAt(0) == '1') {
                usableDigits = usableDigits.substring(1);
            }

            // after removing country code, need exactly 10 digits for complete number
            if (usableDigits.length !== 10) {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
                this.$emit('validated', this.phone_valid);
                return;
            }

            try {
                this.phone_valid = !!E164.isUSPhoneNumber(this.raw_digits);
                this.phone_error_message = (this.phone_touched && !this.phone_valid) ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
            } catch (e) {
                this.phone_valid = false;
                this.phone_error_message = this.phone_touched ? `Please enter a valid ${this.label || 'phone number'} (e.g. 1 (555) 123-4567)` : '';
            }

            this.$emit('validated', this.phone_valid);
        },

        validateEmail(email) {
            if (!email) {
                this.email_valid = !this.requiredBool;
                this.email_error_message = (this.email_touched && this.requiredBool) ? 'Email is required' : '';
                this.$emit('validated', this.email_valid);
                return;
            }

            this.email_valid = Validate.isBlankOrEmailAddress(email);
            this.email_error_message = (this.email_touched && !this.email_valid) ? `Please enter a valid ${this.label || 'email address'} (e.g. user@example.com)` : '';
            this.$emit('validated', this.email_valid);
        },

        handleEmailInput(value) {
            this.local_value = value;
            this.$emit('input', value);
            this.validateEmail(value);
        },

        handleEmailBlur(value = this.local_value) {
            this.email_touched = true;
            this.validateEmail(value);
        },

        calculateAge(dateString) {
            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
                return null; // Invalid format
            }

            const [month, day, year] = dateString.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            // Check if the parsed date is valid (e.g., handles 02/30/2000)
            if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
                return null;
            }

            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDifference = today.getMonth() - birthDate.getMonth();

            if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            return age;
        },

        validateBirthday(value) {
            // 1. Handle empty value
            if (!value) {
                this.birthday_valid = !this.requiredBool;
                this.birthday_error_message = (this.birthday_touched && this.requiredBool) ? `${this.label || 'Birthday'} is required` : '';
                this.$emit('validated', this.birthday_valid);
                return;
            }

            // 2. Validate date format (MM/DD/YYYY)
            if (!Validate.isValidDateFormat(value)) {
                this.birthday_valid = false;
                this.birthday_error_message = (this.birthday_touched) ? `Please enter a valid ${this.label || 'birthday'} (MM/DD/YYYY)` : '';
                this.$emit('validated', this.birthday_valid);
                return;
            }

            // 3. Check for minimum age if the prop is provided
            if (this.minAge !== null && this.minAge > 0) {
                const age = this.calculateAge(value);

                // This handles logically impossible dates like 02/30/2000 that pass the format regex
                if (age === null) {
                    this.birthday_valid = false;
                    this.birthday_error_message = (this.birthday_touched) ? 'Please enter a valid date.' : '';
                    this.$emit('validated', this.birthday_valid);
                    return;
                }

                if (age < this.minAge) {
                    this.birthday_valid = false;
                    this.birthday_error_message = (this.birthday_touched) ? `You must be at least ${this.minAge} years old.` : '';
                    this.$emit('validated', this.birthday_valid);
                    return;
                }
            }

            // 4. If all checks pass, the value is valid
            this.birthday_valid = true;
            this.birthday_error_message = '';
            this.$emit('validated', this.birthday_valid);
        },

        handleBirthdayInput(value) {
            this.local_value = this.$rc.maskMMDDYYYY(value);
            this.$emit('input', this.local_value);
            this.validateBirthday(this.local_value);
        },

        handleBirthdayBlur() {
            this.birthday_touched = true;
            this.validateBirthday(this.local_value);
        },

        focus() {
            if (this.type === 'select') {
                if (this.multiple) {
                    this.$refs.multiSelectToggle?.focus()
                } else {
                    this.$refs.selectToggle?.focus()
                }
            } else {
                this.$refs.input?.focus()
            }
        },

        blur() {
            if (this.type === 'select') {
                if (this.multiple) {
                    this.$refs.multiSelectToggle?.blur()
                } else {
                    this.$refs.selectToggle?.blur()
                }
            } else {
                this.$refs.input?.blur()
            }
        },

        toggleDropdown() {
            if (this.disabled) return
            this.dropdown_open = !this.dropdown_open
        },

        closeDropdown() {
            this.dropdown_open = false
            this.focused_option_index = -1
        },

        onSelectKeydown(event) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.toggleDropdown()
                if (this.dropdown_open) {
                    this.$nextTick(() => {
                        this.focusFirstOption()
                    })
                }
            } else if (event.key === 'Escape') {
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                if (!this.dropdown_open) {
                    this.dropdown_open = true
                    this.$nextTick(() => {
                        this.focusFirstOption()
                    })
                } else {
                    this.focusNextOption()
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                if (this.dropdown_open) {
                    this.focusPreviousOption()
                }
            }
        },

        onSelectFocus(event) {
            this.is_focused = true
            this.$emit('focus', event)
        },

        onSelectBlur(event) {
            this.is_focused = false
            setTimeout(() => {
                const focusedElement = document.activeElement
                const isWithinComponent = this.$el && this.$el.contains(focusedElement)

                if (!isWithinComponent) {
                    this.closeDropdown()
                }
            }, 150)
            this.$emit('blur', event)
        },

        focusToggleButton() {
            if (this.multiple) {
                this.$refs.multiSelectToggle?.focus()
            } else {
                this.$refs.selectToggle?.focus()
            }
        },

        onSelectOptionKeydown(event, optionValue, index) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.selectOption(optionValue)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                this.focusNextOption()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                this.focusPreviousOption()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'Home') {
                event.preventDefault()
                this.focusFirstOption()
            } else if (event.key === 'End') {
                event.preventDefault()
                this.focusLastOption()
            }
        },

        onSelectOptionBlur(event) {
            setTimeout(() => {
                const focusedElement = document.activeElement
                const isWithinComponent = this.$el && this.$el.contains(focusedElement)

                if (!isWithinComponent) {
                    this.closeDropdown()
                }
            }, 150)
        },

        selectOption(value) {
            if (this.disabled) return

            this.selected_value = value
            this.$emit('input', value)
            this.closeDropdown()
            this.focusToggleButton()
        },

        toggleOption(value) {
            if (this.disabled) return

            const option = this.options.find(opt => this.getOptionValue(opt) === value)
            const optionLabel = option ? this.getOptionLabel(option) : value

            const index = this.selected_value.indexOf(value)
            if (index > -1) {
                this.selected_value.splice(index, 1)
                this.screen_reader_announcement = `${optionLabel} deselected. ${this.selected_value.length} of ${this.options.length} options selected.`
            } else {
                this.selected_value.push(value)
                this.screen_reader_announcement = `${optionLabel} selected. ${this.selected_value.length} of ${this.options.length} options selected.`
            }

            setTimeout(() => {
                this.screen_reader_announcement = ''
            }, 1000)

            this.$emit('input', this.selected_value)
        },

        isOptionSelected(value) {
            return this.multiple ? this.selected_value.includes(value) : this.selected_value === value
        },

        onOptionKeydown(event, optionValue, index) {
            if (this.disabled) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.toggleOption(optionValue)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault()
                this.focusNextOption()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                this.focusPreviousOption()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                this.closeDropdown()
                this.focusToggleButton()
            } else if (event.key === 'Home') {
                event.preventDefault()
                this.focusFirstOption()
            } else if (event.key === 'End') {
                event.preventDefault()
                this.focusLastOption()
            }
        },

        focusFirstOption() {
            if (this.options.length > 0) {
                this.focused_option_index = 0
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-0` : `selectOption-0`
                    const firstOption = this.$refs[refName]
                    if (firstOption && firstOption[0]) {
                        firstOption[0].focus()
                    }
                })
            }
        },

        focusLastOption() {
            if (this.options.length > 0) {
                this.focused_option_index = this.options.length - 1
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${this.options.length - 1}` : `selectOption-${this.options.length - 1}`
                    const lastOption = this.$refs[refName]
                    if (lastOption && lastOption[0]) {
                        lastOption[0].focus()
                    }
                })
            }
        },

        focusNextOption() {
            if (this.options.length > 0) {
                const nextIndex = this.focused_option_index < this.options.length - 1
                    ? this.focused_option_index + 1
                    : 0 // wrap to first
                this.focused_option_index = nextIndex
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${nextIndex}` : `selectOption-${nextIndex}`
                    const nextOption = this.$refs[refName]
                    if (nextOption && nextOption[0]) {
                        nextOption[0].focus()
                    }
                })
            }
        },

        focusPreviousOption() {
            if (this.options.length > 0) {
                const prevIndex = this.focused_option_index > 0
                    ? this.focused_option_index - 1
                    : this.options.length - 1 // wrap to last
                this.focused_option_index = prevIndex
                this.$nextTick(() => {
                    const refName = this.multiple ? `option-${prevIndex}` : `selectOption-${prevIndex}`
                    const prevOption = this.$refs[refName]
                    if (prevOption && prevOption[0]) {
                        prevOption[0].focus()
                    }
                })
            }
        },

        onClearKeydown(event) {
            if (this.disabled || this.requiredBool) return

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                this.clearSelection()
            }
        },

        clearSelection() {
            if (this.disabled || this.requiredBool) return

            this.selected_value = ''
            this.$emit('input', '')
            this.focusToggleButton()
        },

        handleClickOutside(event) {
            // close dropdown if clicking outside the entire select component
            if (this.$el && !this.$el.contains(event.target)) {
                this.closeDropdown()
            }
        },

        focusInput() {
            if (this.type !== 'select') {
                this.$refs.input?.focus()
            }
        }
    },

    watch: {
        value(newValue) {
            if (this.type === 'tel') {
                const digits = this.getDigitsOnly(newValue);
                this.raw_digits = digits;

                if (digits.length <= 11) {
                    this.local_value = digits ? this.maskDigits(digits) : '';
                } else {
                    this.local_value = newValue;
                }
                this.validatePhoneDigits();
            } else if (this.type === 'email') {
                this.local_value = newValue;
                this.validateEmail(newValue);
            } else if (this.type === 'birthday') {
                this.local_value = newValue;
                this.validateBirthday(newValue);
            } else if (this.type === 'text') {
                this.local_value = newValue;
            } else if (this.type === 'select') {
                if (this.multiple) {
                    this.selected_value = Array.isArray(newValue) ? newValue : [];
                } else {
                    this.selected_value = newValue;
                }
            }
        },

        multiple(is_now_multiple) {
            if (this.type === 'select') {
                this.selected_value = is_now_multiple ? [] : '';
                this.$emit('input', this.selected_value);
            }
        }
    },

    mounted() {
        if (this.type === 'tel') {
            const digits = this.getDigitsOnly(this.value);
            this.raw_digits = digits;

            if (digits) {
                if (digits.length <= 11) {
                    this.local_value = this.maskDigits(digits);
                } else {
                    this.local_value = this.value;
                }
                if (this.value) {
                    this.phone_touched = true;
                    this.validatePhoneDigits();
                }
            }
        } else if (this.type === 'email') {
            this.local_value = this.value;
            if (this.value) {
                this.email_touched = true;
                this.validateEmail(this.value);
            }
        } else if (this.type === 'birthday') {
            this.local_value = this.value;
            if (this.value) {
                this.birthday_touched = true;
                this.validateBirthday(this.value);
            }
        } else if (this.type === 'text') {
            this.local_value = this.value;
        } else if (this.type === 'select') {
            if (this.multiple) {
                this.selected_value = Array.isArray(this.value) ? this.value : [];
            } else {
                this.selected_value = this.value;
            }
            document.addEventListener('click', this.handleClickOutside);
        }

        if (this.autofocus) {
            this.$nextTick(() => {
                this.focusInput();
            });
        }
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }
}
</script>

<style lang="scss" scoped>
// https://accessibleweb.com/color-contrast-checker/
.rc-base-input {
    --text-default: #6b7280;
    --text-dark: #374151;
    --bg-selected: #767676;
    /* 4.54:1 on white (3:1 minimum) */
    --text-on-selected: #fff;
    /* 4.54:1 on --bg-selected (4.5:1 minimum) */
    --error: #d2233c;
    --dark-grey: #111827;
    --border-color: #c9c9c9;
    --bg-disabled: #f9fafb;
    --primary: var(--v-primary-base, #f24653);
    --color-placeholder: #9ca3af;

    margin-bottom: 1rem;
}

.input-wrapper,
.select-wrapper {
    position: relative;
}

.input-container {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
}

.append-outer-slot {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.input-label {
    display: block;
    font-weight: 500;
    color: var(--text-dark);
    margin-bottom: 0.2rem;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &.underlined {
        margin-bottom: 0;
    }

    &.required {
        .required-indicator {
            color: var(--error);
        }
    }
}

.form-input {
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.2;
    color: var(--text-dark);
    background-color: #ffffff;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &.input-underlined {
        border: none;
        border-bottom: 2px solid var(--border-color);
        border-radius: 0;
        padding-left: 0;
        padding-right: 0;
        background-color: transparent;
    }

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &.input-underlined:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }

    &.input-error {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.input-underlined.input-error {
        border-bottom-color: var(--error);

        &:focus {
            border-bottom-color: var(--error);
            box-shadow: none;
        }
    }

    &.input-disabled {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }

    &.input-underlined.input-disabled {
        background-color: transparent;
        border-bottom-color: var(--text-default);
    }

    &.input-readonly {
        background-color: var(--bg-disabled);
    }

    &.input-underlined.input-readonly {
        background-color: transparent;
    }

    &::placeholder {
        color: var(--color-placeholder);
    }

    &.has-prepend-icon {
        padding-left: 2.75rem;
    }
}

.input-container {
    position: relative;

    &.has-icon {
        .input-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-default);
            z-index: 1;
            pointer-events: none;
            transition: color 0.15s ease-in-out;

            &.input-icon-focused {
                color: var(--dark-grey);
            }
        }
    }
}

.select-container {
    position: relative;

    &.select-error .form-select-button {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.select-disabled .form-select-button {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }
}

.form-select-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.2rem 2.5rem 0.25rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--text-dark);
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    text-align: left;

    &.has-clear-button {
        padding-right: 4.5rem;
    }

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.multi-select-dropdown-open .multi-select-toggle,
.select-dropdown-open .form-select-button {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.select-dropdown-open-top .form-select-button,
.multi-select-dropdown-open-top .multi-select-toggle {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
}

.select-dropdown-open-bottom .form-select-button,
.multi-select-dropdown-open-bottom .multi-select-toggle {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
}

.select-underlined .form-select-button {
    border: none;
    border-bottom: 2px solid var(--border-color);
    border-radius: 0;
    padding-left: 0;
    background-color: transparent;

    &:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }
}

.select-underlined.select-error .form-select-button {
    border-bottom-color: var(--error);

    &:focus {
        border-bottom-color: var(--error);
        box-shadow: none;
    }
}

.select-underlined.select-disabled .form-select-button {
    background-color: transparent;
    border-bottom-color: var(--text-default);
}

.select-value {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
        color: var(--color-placeholder);
    }
}

.select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 0.25rem 0.25rem;
    box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.15), 0 2px 8px -2px rgba(0, 0, 0, 0.1);
    max-height: 260px;
    overflow-y: auto;

    &.select-dropdown-top {
        top: auto;
        bottom: 100%;
        border-top: 1px solid var(--border-color);
        border-bottom: 1px solid var(--border-color);
        border-radius: 0.25rem 0.25rem 0 0;
        box-shadow: 0 -4px 12px -2px rgba(0, 0, 0, 0.15), 0 -2px 8px -2px rgba(0, 0, 0, 0.1);
    }
}

.select-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f3f4f6;
    }

    &.selected {
        background-color: var(--bg-selected);
        font-weight: bold;

        .select-option-label {
            color: var(--text-on-selected);
        }

        &.focused {
            background-color: var(--bg-selected);
        }
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: -2px;
        background-color: #f3f4f6;
    }
}

.select-option-label {
    color: var(--text-dark);
    flex: 1;
    font-size: 0.95rem;
}

.select-arrow {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-default);

    svg {
        transition: transform 0.15s ease;

        &.rotated {
            transform: translateY(-50%) rotate(180deg);
        }
    }
}

.select-clear-button {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    color: var(--primary);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:hover,
    &:focus {
        background-color: rgba(242, 70, 83, 0.1);
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: 2px;
    }

    svg {
        width: 20px;
        height: 20px;
    }
}

.multi-select-container {
    position: relative;

    &.multi-select-error .multi-select-toggle {
        border-color: var(--error);

        &:focus {
            border-color: var(--error);
            box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
    }

    &.multi-select-disabled .multi-select-toggle {
        background-color: var(--bg-disabled);
        color: var(--text-default);
        cursor: not-allowed;
    }
}

.multi-select-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.4rem 2.5rem 0.4rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--text-dark);
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        outline: none;
        border-color: var(--dark-grey);
        box-shadow: 0 0 0 3px rgba(53, 53, 53, 0.1);
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.multi-select-underlined .multi-select-toggle {
    border: none;
    border-bottom: 2px solid var(--border-color);
    border-radius: 0;
    padding-left: 0;
    background-color: transparent;

    &:focus {
        border-bottom-color: var(--dark-grey);
        box-shadow: none;
    }
}

.multi-select-underlined.multi-select-error .multi-select-toggle {
    border-bottom-color: var(--error);

    &:focus {
        border-bottom-color: var(--error);
        box-shadow: none;
    }
}

.multi-select-underlined.multi-select-disabled .multi-select-toggle {
    background-color: transparent;
    border-bottom-color: var(--text-default);
}

.multi-select-value {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
        color: var(--color-placeholder);
    }
}

.multi-select-arrow {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-default);
    transition: transform 0.2s ease;

    &.rotated {
        transform: translateY(-50%) rotate(180deg);
    }
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 0.25rem 0.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;

    &.multi-select-dropdown-top {
        top: auto;
        bottom: 100%;
        border-top: 1px solid var(--border-color);
        border-bottom: none;
        border-radius: 0.25rem 0.25rem 0 0;
        box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
    }
}

.multi-select-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    accent-color: var(--primary);
    transition: background-color 0.15s ease;

    &:hover,
    &.focused {
        background-color: #f3f4f6;
    }

    &.selected {
        background-color: #e5e7eb;
    }

    &.focused.selected {
        background-color: #d1d5db;
    }

    &:focus {
        outline: 2px solid var(--dark-grey);
        outline-offset: -2px;
        background-color: #f3f4f6;
    }
}

.multi-select-checkbox {
    margin-right: 0.5rem;
    pointer-events: none;
}

.multi-select-option-label {
    flex: 1;
}

.error-message {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--error);
    line-height: 1.25rem;
}

.hint-text {
    margin-top: 2px;
    font-size: 0.875rem;
    color: var(--text-default);
    line-height: 1.25rem;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@media (prefers-contrast: high) {

    .form-input {
        border-width: 3px;

        &:focus {
            box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.3);
        }
    }
}

@media (prefers-reduced-motion: reduce) {

    .form-input {
        transition: none !important;
    }
}
</style>